import classNames from 'classnames';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import useHeightAnimation from 'hooks/useHeightAnimation';
import { isUrl as isUrlFunc } from 'utils';

import './BussinessVideo.styles.scss';
import './BussinessVideo.styles.responsive.scss';

const truncateText = (text: string, maxLength: number) =>
  text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

const MAX_NUM_OF_CHAR = 162;

function isValidHtml(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
}

function addAutoplayToEmbeddedVideo(embeddedVideo: string) {
  if (!embeddedVideo || !embeddedVideo.includes('iframe')) return;

  return embeddedVideo.replace(/src="([^"]*)"/, (_, match) => {
    const url = new URL(match);

    url.searchParams.set('autoplay', '1');
    url.searchParams.set('mute', '1');

    return `src="${url.toString()}"`;
  });
}

type BussinessVideoProps = {
  className?: string;
  text?: string;
  videoSrc: string;
  maxNumOfChar?: number;
  hasVideo?: boolean;
};

const BussinessVideo: FC<BussinessVideoProps> = ({
  className,
  text,
  videoSrc,
  hasVideo,
  maxNumOfChar = MAX_NUM_OF_CHAR,
}) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);

  const [container, containerRef] = useCallbackRef<HTMLDivElement>();

  useHeightAnimation({
    element: container,
    defaultOpen: true,
  });

  const isUrl = isUrlFunc(videoSrc);

  if (!videoSrc || (!isUrl && !isValidHtml(videoSrc)) || !hasVideo) return null;

  return (
    <div className={classNames('business-teaser-video', className)}>
      {isUrl ? (
        <video src={videoSrc} controls autoPlay muted />
      ) : (
        <div
          dangerouslySetInnerHTML={{
            __html: addAutoplayToEmbeddedVideo(videoSrc),
          }}
        />
      )}

      {text && (
        <>
          <p className="business-teaser-video__title">{t('videoTranscript')}</p>
          <div className="business-teaser-video__text" ref={containerRef}>
            {showMore ? text : truncateText(text, maxNumOfChar)}
            &nbsp;
            {!showMore && (
              <Button variant="link" onClick={() => setShowMore(true)}>
                {t('readMore')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BussinessVideo;
